import type { RoundSummary, RoundPlayer } from '../../api/client.js';
import type { WorldState, Dyn, Tab } from '../RoundScreen.js';

const HEBREW_MONTHS = [
  'תשרי', 'חשון', 'כסלו', 'טבת', 'שבט', 'אדר',
  'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול',
];

const RES_ICONS: Record<string, string> = {
  gold: '💰',
  torah: '📖',
  kdusha: '🕯️',
  authority: '👑',
  influence: '🎙️',
  politicalPower: '🏛️',
};

const RES_LABELS: Record<string, string> = {
  gold: 'זהב',
  torah: 'תורה',
  kdusha: 'קדושה',
  authority: 'סמכות',
  influence: 'השפעה',
  politicalPower: 'כוח',
};

export function TopBar({
  round,
  state,
  player,
  playerDynasty,
  onLeave,
  tab,
  onTabChange,
}: {
  round: RoundSummary;
  state: WorldState;
  player: RoundPlayer;
  playerDynasty?: Dyn;
  onLeave: () => void;
  tab: Tab;
  onTabChange: (t: Tab) => void;
}) {
  const DAYS_PER_MONTH = 30;
  const monthOfYear = Math.ceil(state.meta.currentDay / DAYS_PER_MONTH);
  const dayOfMonth = ((state.meta.currentDay - 1) % DAYS_PER_MONTH) + 1;
  const monthName = HEBREW_MONTHS[monthOfYear - 1] ?? '?';
  const year = state.meta.currentYear;
  const progress = (state.meta.tickNumber / round.total_ticks) * 100;

  return (
    <header className="topbar">
      <div className="topbar-left">
        <button className="btn-back" onClick={onLeave} title="חזור ל-Lobby">
          ⬅
        </button>
        <div className="brand">
          <span className="brand-name">חצרות</span>
          <span className="brand-round">{round.name}</span>
        </div>
      </div>

      <nav className="topbar-tabs">
        <button className={tab === 'map' ? 'active' : ''} onClick={() => onTabChange('map')}>
          🗺 מפה
        </button>
        <button className={tab === 'dashboard' ? 'active' : ''} onClick={() => onTabChange('dashboard')}>
          🏠 החצר שלי
        </button>
        <button className={tab === 'rankings' ? 'active' : ''} onClick={() => onTabChange('rankings')}>
          🏆 דירוגים
        </button>
      </nav>

      {playerDynasty && (
        <div className="topbar-resources">
          {(['gold', 'torah', 'kdusha', 'authority', 'influence', 'politicalPower'] as const).map((k) => (
            <div key={k} className="res-pill" title={RES_LABELS[k]}>
              <span className="icon">{RES_ICONS[k]}</span>
              <span className="val">{fmt(playerDynasty.resources[k] ?? 0)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="topbar-right">
        <div className="date">
          <span>{dayOfMonth} ב{monthName}</span>
          <span className="year">תש״{year - 5000}</span>
        </div>
        <div className="progress" title={`${state.meta.tickNumber} / ${round.total_ticks} ימים`}>
          <div className="bar" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* player badge */}
      <div className="player-badge" title={player.dynasty_name}>
        {player.dynasty_name.slice(0, 12)}
      </div>
    </header>
  );
}

function fmt(n: number): string {
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 10_000) return (n / 1000).toFixed(1) + 'K';
  if (Math.abs(n) >= 1_000) return Math.floor(n).toLocaleString('he-IL');
  return String(Math.floor(n));
}
